// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ONE_HOUR_MS } from '@/utilities/time-constants';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Per-tab record of when the subscription notice was last acknowledged. It is
 * deliberately session scoped so every new tab shows the notice again.
 */
export const SUBSCRIPTION_NOTICE_ACKNOWLEDGED_AT_KEY =
  'hardfin.subscription-notice-acknowledged-at';

/**
 * How long a tab must stay hidden before the notice is shown again on return.
 */
const SUBSCRIPTION_NOTICE_REPROMPT_MS = ONE_HOUR_MS;

/**
 * Reads the stored acknowledgement. Any storage failure, such as a browser
 * that blocks session storage, is treated as "not acknowledged".
 */
function readAcknowledgedAtMs(): number | null {
  try {
    const stored = window.sessionStorage.getItem(
      SUBSCRIPTION_NOTICE_ACKNOWLEDGED_AT_KEY
    );

    if (stored === null) {
      return null;
    }

    const acknowledgedAtMs = Number(stored);

    return Number.isFinite(acknowledgedAtMs) ? acknowledgedAtMs : null;
  } catch (_) {
    return null;
  }
}

function writeAcknowledgedAtMs(acknowledgedAtMs: number): void {
  try {
    window.sessionStorage.setItem(
      SUBSCRIPTION_NOTICE_ACKNOWLEDGED_AT_KEY,
      String(acknowledgedAtMs)
    );
  } catch (_) {
    // A tab that cannot store the acknowledgement simply shows the notice
    // again on its next visit.
  }
}

interface SubscriptionNoticeAcknowledgement {
  isOpen: boolean;
  acknowledge: () => void;
}

/**
 * A React hook that decides whether the subscription expiration notice should be
 * shown. It opens for a tab that has not acknowledged the notice, and opens
 * again when the user returns to a tab that was hidden for an hour or more.
 */
export function useSubscriptionNoticeAcknowledgement(): SubscriptionNoticeAcknowledgement {
  const [isOpen, setIsOpen] = useState(() => readAcknowledgedAtMs() === null);
  const hiddenAtMsRef = useRef<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtMsRef.current = Date.now();
        return;
      }

      const hiddenAtMs = hiddenAtMsRef.current;
      hiddenAtMsRef.current = null;

      if (
        hiddenAtMs !== null &&
        Date.now() - hiddenAtMs >= SUBSCRIPTION_NOTICE_REPROMPT_MS
      ) {
        setIsOpen(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const acknowledge = useCallback(() => {
    writeAcknowledgedAtMs(Date.now());
    setIsOpen(false);
  }, []);

  return { isOpen, acknowledge };
}
